import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { useDispatch, useSelector } from "@src/store";
import { selectedExperience } from "@src/store/slices/experiences";
import { setAiChat, setBypassUnsavedChanges } from "@src/store/slices/global";
import { EXPERIMENT_ROUTES, PERSONALIZATION_ROUTES } from "@src/routes/routes";

import { markSyntheticCategorySwap } from "./experience-route-swap-flag";

/**
 * Experiments and personalizations are rendered by two entirely separate
 * route trees (`ExperienceWrapperRoute` / `PersonalizationExperienceRoute`) —
 * there's no shared parent that picks between them based on `category`.
 * `setExperienceMode` (fired when the AI assistant flips category mid-chat)
 * only updates Redux; the URL/mounted route never changes. The merchant is
 * left on the wrong route's component tree — e.g. a personalization's page
 * showing `PersonalizationExperienceBody`, whose tab switch has no
 * `TEST_GROUPS` case and falls through to Analytics content.
 *
 * This can't live inside `useSetViewActionAnyExperience` — that hook only
 * dispatches Redux tab state, it has no route-changing capability, and it's
 * mounted per-route anyway (it wouldn't survive the swap it needs to
 * trigger). This watches for the experience CURRENTLY on screen having its
 * category diverge from the route it's mounted on, and swaps to the
 * matching route via client-side `navigate()` — no full page reload, so the
 * AI chat (mounted outside the route tree in `AppLayoutShell`) stays alive.
 *
 * Handles both the saved-experience route (`/personalization/:id` <->
 * `/experiment/:id`) and the unsaved-draft "create" route (bare
 * `/personalization` <-> `/experiment`, no id segment) — a brand new
 * experience being built live in chat has no id yet, and category flips
 * happen freely before the merchant ever hits Save.
 *
 * Three more things this swap has to defeat, all aimed at REAL exits and all
 * wrong for this synthetic one:
 * - `ExperimentProvider`/`PersonalizationExperienceProvider`'s `useBlocker`
 *   predicate checks `isSyntheticCategorySwapPending()` live, at whatever
 *   moment react-router actually calls it — not just a reactive "already
 *   blocked" cleanup — since that predicate isn't guaranteed to run in the
 *   same synchronous tick as `navigate()`. `setBypassUnsavedChanges(true)`
 *   is a secondary guard, same convention as `rollout-experience.ts`.
 * - `useClearExperienceOnUnmount` wipes every experience-scoped Redux slice
 *   when the old route unmounts, as if the merchant left for good. For an
 *   unsaved draft (no id, nothing to refetch from) that destroys the very
 *   data this swap is trying to carry over. `markSyntheticCategorySwap()`
 *   tells that cleanup to skip the wipe this one time.
 * - `LoggedInProvider` watches `location.pathname` and clears/closes the AI
 *   chat on any change, assuming the merchant navigated to an unrelated page
 *   or experience — exactly the pathname change this hook's own `navigate()`
 *   produces, so left unchecked it undoes the chat re-open below on every
 *   swap.
 *
 * The flag is cleared by `LoggedInProvider`, not here: its effect is keyed
 * off `location.pathname`, which for a data router only updates once the
 * navigation (including the blocker's decision) has fully resolved — making
 * it the last of the flag's consumers to see it as pending, and so the
 * correct, deterministic place to end the swap's grace period. Clearing on
 * a guessed timeout here instead previously raced against exactly that
 * router-internal delay.
 *
 * Also re-opens the AI chat in `experience-creation-single-agent` mode on
 * the new route — the swap happens mid-conversation (the AI just flipped
 * category), so the merchant should land back in that same conversation, not
 * a bare page with the chat closed.
 *
 * Mounted once, high in the tree (`AppLayoutShell`), not per-route — it has
 * to survive the route swap itself to do its job.
 */
export const useRedirectOnCategorySwitch = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const experience = useSelector(selectedExperience);
  const previousCategory = useRef<string | undefined>(experience?.category);

  useEffect(() => {
    const prior = previousCategory.current;
    previousCategory.current = experience?.category;

    if (!experience?.category) return;
    if (!prior || prior === experience.category) return;

    // Normalize the trailing slash before matching. The create-mode links are
    // built as `${route}/?action=new&...` (see `getTestLink`), so
    // `location.pathname` is "/experiment/" — which a strict comparison against
    // "/experiment" misses, leaving the merchant on the old route tree with the
    // new category. That mattered little while saved experiences could also
    // switch (they matched via the `/${id}` branch below); now that switching
    // is restricted to never-saved drafts, the create route is the ONLY shape
    // that reaches here.
    const path = window.location.pathname.replace(/\/+$/, "");
    const id = experience.id;
    const onPersonalizationRoute =
      path === PERSONALIZATION_ROUTES.Personalization ||
      (!!id &&
        path.startsWith(`${PERSONALIZATION_ROUTES.Personalization}/${id}`));
    const onExperimentRoute =
      path === EXPERIMENT_ROUTES.Experiment ||
      (!!id && path.startsWith(`${EXPERIMENT_ROUTES.Experiment}/${id}`));

    const target = id ? `/${id}` : "";
    // `useAndSetGenericExperimentFromUrl`'s create-mode branch reads its
    // initial `type` from `?type=` — without it, a genuine (non-swap) mount
    // of this URL falls back to `null`. Carrying the already-correct
    // `experience.type` through the URL keeps that path honest independent
    // of the synthetic-swap skip below; personalization has no content
    // sub-type, so this only applies to the experiment target.
    const experimentTarget = `${EXPERIMENT_ROUTES.Experiment}${target}?type=${experience.type}`;

    let navigated = false;
    if (experience.category === "experiment" && onPersonalizationRoute) {
      dispatch(setBypassUnsavedChanges(true));
      markSyntheticCategorySwap();
      navigate(experimentTarget, { replace: true });
      navigated = true;
    } else if (experience.category === "personalization" && onExperimentRoute) {
      dispatch(setBypassUnsavedChanges(true));
      markSyntheticCategorySwap();
      navigate(`${PERSONALIZATION_ROUTES.Personalization}${target}`, {
        replace: true,
      });
      navigated = true;
    }
    // Deliberately no `?tab=` on either target — a stale one from the old
    // route type would re-override the tab `setExperienceMode` already set
    // correctly. The fresh mount's `useSetViewActionAnyExperience` picks a
    // sensible default tab for the new category on its own.

    if (navigated) {
      dispatch(
        setAiChat([
          { key: "open", value: true },
          { key: "mode", value: "experience-creation-single-agent" },
        ])
      );
      const timeout = setTimeout(
        () => dispatch(setBypassUnsavedChanges(false)),
        0
      );
      return () => clearTimeout(timeout);
    }
  }, [experience?.id, experience?.category, navigate, dispatch]);
};
